<script>
  /**
   * Pantalla principal.
   *
   * Lo primero que se ve es cuánta plata hay, porque es la pregunta que uno
   * abre la app a resolver. Lo segundo es en qué se fue. Nada más compite por
   * la atención en la primera pantalla.
   */
  import { db, CUENTAS_PASIVO, crearMovimiento, crearAbono, hoy } from './lib/db.js';
  import { buscarCategoria } from './lib/categorias.js';
  import { disponibleEn, compromisosDelMes } from './lib/libre.js';
  import { formatear, formatearAria, formatearCorto } from './lib/dinero.js';
  import { alCambiar, pendientesDeSubir, guardar } from './lib/repositorio.js';
  import { Api } from './lib/api.js';
  import { Sincronizador, ESTADO } from './lib/sincronizacion.js';
  import { leer as leerConfig, estaConfigurada } from './lib/configuracion.js';
  import FilaMovimiento from './componentes/FilaMovimiento.svelte';
  import HojaRegistro from './componentes/HojaRegistro.svelte';
  import Configuracion from './componentes/Configuracion.svelte';
  import PantallaCuentas from './componentes/PantallaCuentas.svelte';
  import HojaComprobante from './componentes/HojaComprobante.svelte';
  import PantallaInformes from './componentes/PantallaInformes.svelte';
  import PantallaImportar from './componentes/PantallaImportar.svelte';
  import Avisos from './componentes/Avisos.svelte';
  import PantallaContratos from './componentes/PantallaContratos.svelte';
  import PantallaPrestamos from './componentes/PantallaPrestamos.svelte';
  import PantallaCuotas from './componentes/PantallaCuotas.svelte';
  import PantallaSuscripciones from './componentes/PantallaSuscripciones.svelte';
  import { cobrosParaAnotar, movimientoDelCobro, idDelGmfDelCobro, resumenFijos, COBRO, idDeterminista } from './lib/recurrentes.js';
  import { gmfDe, movimientoGmf } from './lib/gmf.js';
  import HojaMovimiento from './componentes/HojaMovimiento.svelte';
  import { fotografiar, evaluar } from './lib/coaching.js';
  import { cuotasDelMes, planDeCuotas, paramsDe, debitosAutomaticos, estadoPlan } from './lib/cuotas.js';
  import { totales as totalesDeudas } from './lib/deudas.js';

  let { primeraVez = false, persistencia = null } = $props();

  // Un solo sincronizador para toda la app. Se crea aquí y se le pasa a la
  // pantalla de configuración, para que no haya dos hablando con el servidor.
  const config = leerConfig();
  const api = new Api({ url: config.url, secreto: config.secreto });
  const sincronizador = new Sincronizador(api);

  let estadoSync = $state({ estado: ESTADO.REPOSO, ultimoError: null });
  let configAbierta = $state(false);
  let cuentasAbiertas = $state(false);
  let camaraAbierta = $state(false);
  let informesAbiertos = $state(false);
  let importarAbierto = $state(false);
  let movimientoAbierto = $state(null);
  let tarjetas = $state([]);
  let contratos = $state([]);
  let contratosAbiertos = $state(false);
  let prestamos = $state([]);
  let prestamosAbiertos = $state(false);
  let compras = $state([]);
  let abonos = $state([]);
  let cuotasAbiertas = $state(false);
  let deudaEnfocada = $state(null);
  let compraEnfocada = $state(null);
  let recurrentes = $state([]);
  let suscripcionesAbiertas = $state(false);
  let cobrosSolos = $state([]);   // lo que se anotó solo en esta sesión, para decirlo

  /**
   * Un movimiento de un préstamo o de una compra a cuotas se corrige en su
   * pantalla, no en la hoja de movimiento. Esto la abre en el lugar exacto.
   */
  function irA({ tipo, id }) {
    if (tipo === 'deuda') {
      deudaEnfocada = id;
      prestamosAbiertos = true;
    } else if (tipo === 'compra') {
      compraEnfocada = id;
      cuotasAbiertas = true;
    }
  }
  let descartados = $state(leerDescartados());

  /** Los avisos cerrados se recuerdan por dispositivo, no se sincronizan. */
  function leerDescartados() {
    try { return JSON.parse(localStorage.getItem('avisos_cerrados') || '{}'); }
    catch { return {}; }
  }
  function descartar(id) {
    descartados = { ...descartados, [id]: Date.now() };
    try { localStorage.setItem('avisos_cerrados', JSON.stringify(descartados)); } catch {}
  }
  let entidades = $state([]);

  let cuentas = $state([]);
  let categorias = $state([]);
  let movimientos = $state([]);
  let pendientes = $state(0);
  let hojaAbierta = $state(false);
  let cargando = $state(true);

  const porId = (lista) => Object.fromEntries(lista.map((x) => [x.id, x]));

  /**
   * Las cuentas que se muestran en el carrusel.
   * "Me deben" y "Le debo a gente" son cuentas de verdad —cuentan en el
   * patrimonio— pero no se muestran sueltas: las representa la tarjeta de
   * Préstamos, que dice además a quién y desde cuándo. Mostrar las dos era
   * ver el mismo número dos veces con dos nombres distintos.
   */
  const cuentasVisibles = $derived(
    cuentas.filter((c) => c.tipo !== 'por_cobrar' && c.tipo !== 'por_pagar')
  );
  const deudasPersonas = $derived(totalesDeudas(prestamos));
  const cuentasPorId = $derived(porId(cuentas));
  const categoriasPorId = $derived(porId(categorias));

  /**
   * Saldo de cada cuenta: lo que entró menos lo que salió.
   * Una transferencia resta en una cuenta y suma en la otra, así que el
   * patrimonio total no se mueve. Ese es justo el punto.
   */
  const saldos = $derived.by(() => {
    const mapa = Object.fromEntries(cuentas.map((c) => [c.id, c.saldo_inicial || 0]));
    for (const m of movimientos) {
      if (m.borrado_en) continue;
      if (m.cuenta_origen && mapa[m.cuenta_origen] !== undefined) {
        mapa[m.cuenta_origen] -= m.monto;
      }
      if (m.cuenta_destino && mapa[m.cuenta_destino] !== undefined) {
        mapa[m.cuenta_destino] += m.monto;
      }
    }
    return mapa;
  });

  /**
   * Patrimonio: lo que tienes menos lo que debes.
   * Las tarjetas de crédito y los créditos rotativos restan, porque son deuda.
   */
  const patrimonio = $derived(
    cuentas
      .filter((c) => c.incluir_en_patrimonio !== false && !c.archivada)
      .reduce((total, c) => {
        const saldo = saldos[c.id] ?? 0;
        return total + (CUENTAS_PASIVO.includes(c.tipo) ? -Math.abs(saldo) : saldo);
      }, 0)
  );

  /** Los movimientos agrupados por día, del más reciente al más viejo. */
  const porDia = $derived.by(() => {
    const grupos = new Map();
    for (const m of movimientos) {
      if (m.borrado_en) continue;
      if (!grupos.has(m.fecha)) grupos.set(m.fecha, []);
      grupos.get(m.fecha).push(m);
    }
    return [...grupos.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  });

  // La lista muestra de a 60 días; los saldos usan TODO (ver `cargar`).
  let diasVisibles = $state(60);
  const porDiaVisible = $derived(porDia.slice(0, diasVisibles));

  const gastoDelMes = $derived(
    movimientos
      .filter((m) => !m.borrado_en && m.tipo === 'egreso' && m.fecha.startsWith(mesActual()))
      .reduce((t, m) => t + m.monto, 0)
  );

  /**
   * Las cuotas que caen este mes, sumando TODAS las compras diferidas.
   *
   * Es el numero con el que uno decide: nadie paga el total de lo que debe
   * en Addi, paga las cuotas del mes.
   */
  const cuotasEsteMes = $derived.by(() => {
    const vivas = compras.filter((c) => !c.pagada).map((c) => ({
      ...c,
      plan: c.plan?.length ? c.plan : planDeCuotas(paramsDe(
        c, tarjetas.find((t) => t.cuenta_id === c.cuenta_id) ?? null
      )),
      abonos: abonos.filter((a) => a.compra_id === c.id),
    }));
    return cuotasDelMes(vivas, mesActual());
  });

  function mesActual() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  function etiquetaDia(fecha) {
    const hoy = new Date();
    const ayer = new Date(Date.now() - 86400000);
    const iso = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (fecha === iso(hoy)) return 'Hoy';
    if (fecha === iso(ayer)) return 'Ayer';
    return new Date(fecha + 'T12:00:00').toLocaleDateString('es-CO', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  }

  async function cargar() {
    const [c, cat, mov, pend, ent, tar, con, deu, com, abo, rec] = await Promise.all([
      db.cuentas.toArray(),
      db.categorias.toArray(),
      // TODOS, no los últimos 200. Los saldos salen de sumar cada movimiento
      // desde el principio: con un tope, el día 201 el saldo de cada cuenta
      // habría empezado a mentir en silencio. Unos miles de filas en
      // IndexedDB se leen en milisegundos; la lista de abajo sí va por partes.
      db.movimientos.toArray(),
      pendientesDeSubir(),
      db.entidades.toArray(),
      db.tarjetas.toArray(),
      db.contratos.toArray(),
      db.deudas.toArray(),
      db.compras_diferidas.toArray(),
      db.abonos.toArray(),
      db.recurrentes.toArray(),
    ]);
    entidades = ent.filter((x) => !x.borrado_en);
    tarjetas = tar.filter((x) => !x.borrado_en);
    contratos = con.filter((x) => !x.borrado_en);
    prestamos = deu.filter((x) => !x.borrado_en && x.tipo === 'personal');
    compras = com.filter((x) => !x.borrado_en);
    abonos = abo.filter((x) => !x.borrado_en);
    recurrentes = rec.filter((x) => !x.borrado_en);
    cuentas = c.filter((x) => !x.borrado_en && !x.archivada).sort((a, b) => a.orden - b.orden);
    categorias = cat.filter((x) => !x.borrado_en);
    movimientos = mov
      .filter((x) => !x.borrado_en)
      .sort((a, b) => (b.fecha + (b.hora ?? '')).localeCompare(a.fecha + (a.hora ?? '')));
    pendientes = pend;
    cargando = false;
    anotarCobrosAutomaticos();
    debitarCuotasAutomaticas();
    resolverCategoriasPorNombre();
  }

  /**
   * Las cuotas que el banco cobra solo, como las del crédito de Nequi.
   *
   * Nequi no espera a que uno marque "pagar": el día de la cuota se lleva lo
   * que haya, y si no alcanza, se lleva el resto apenas entra plata. Si la
   * app no hiciera lo mismo, el saldo de Nequi quedaría inflado desde el día
   * de la cuota y la cuota saldría "atrasada" cuando ya se pagó.
   *
   * El cálculo está en lib/cuotas.js (debitosAutomaticos). Aquí solo se anota,
   * con ids fijos: si el cobro ya existe —o se borró a propósito—, no se toca.
   */
  let debitando = false;
  async function debitarCuotasAutomaticas() {
    if (debitando) return;
    const conDebito = compras.filter((c) =>
      !c.pagada && c.cobro === 'debito_automatico' && c.cuenta_debito_id);
    if (!conDebito.length) return;
    debitando = true;
    try {
      const anotados = [];
      for (const c of conDebito) {
        const cuenta = cuentas.find((x) => x.id === c.cuenta_debito_id);
        if (!cuenta) continue;
        const plan = c.plan?.length
          ? c.plan
          : planDeCuotas(paramsDe(c, tarjetas.find((t) => t.cuenta_id === c.cuenta_id) ?? null));
        let propios = abonos.filter((a) => a.compra_id === c.id);
        const debitos = debitosAutomaticos({
          plan, abonos: propios, movimientos, cuentaId: cuenta.id,
          saldoInicial: cuenta.saldo_inicial || 0, hoy: hoy(),
        });

        for (const d of debitos) {
          const semilla = `debito:${c.id}:${d.pagadoAntes}`;
          const id = idDeterminista(d.fecha, semilla);
          if (await db.movimientos.get(id)) break;

          const cuotas = d.cubre.length === 1 ? `Cuota ${d.cubre[0]}` : `Cuotas ${d.cubre.join(', ')}`;
          const gmf = gmfDe({
            tipo: 'transferencia', monto: d.monto, cuentaOrigen: cuenta,
            entidad: entidades.find((e) => e.id === cuenta.entidad_id) ?? null,
            metodoPago: 'debito_automatico',
          }).monto;
          const traslado = {
            ...crearMovimiento({
              tipo: 'transferencia', monto: d.monto, fecha: d.fecha,
              concepto: `${cuotas} de ${plan.length} · ${c.descripcion}`,
              contraparte: c.comercio, metodo_pago: 'debito_automatico',
              cuenta_origen: cuenta.id, cuenta_destino: c.cuenta_id,
              compra_diferida_id: c.id, origen_dato: 'automatico',
              gmf, gmf_revisado: true,
              notas: `${cuenta.nombre} lo descontó solo.`,
            }),
            id,
          };
          await guardar('movimientos', traslado);
          if (gmf > 0) {
            await guardar('movimientos', {
              ...crearMovimiento(movimientoGmf(traslado, gmf)),
              id: idDeterminista(d.fecha, `${semilla}:gmf`),
            });
          }
          // El interés es lo único que es gasto nuevo: lo que cuesta el crédito.
          if (d.interes > 0) {
            await guardar('movimientos', {
              ...crearMovimiento({
                tipo: 'egreso', monto: d.interes, fecha: d.fecha,
                concepto: `Intereses · ${c.descripcion}`, contraparte: c.comercio,
                cuenta_origen: c.cuenta_id, compra_diferida_id: c.id,
                metodo_pago: 'debito_automatico', origen_dato: 'automatico',
                categoria_id: buscarCategoria(categorias, 'Intereses de préstamos')?.id ?? null,
                notas: 'Lo que costó el crédito este mes.',
              }),
              id: idDeterminista(d.fecha, `${semilla}:interes`),
            });
          }
          const antes = estadoPlan(plan, propios, hoy());
          const abono = {
            ...crearAbono({
              compra_id: c.id, movimiento_id: id, clase: 'abono', fecha: d.fecha,
              monto: d.monto, cuenta_id: cuenta.id, metodo_pago: 'debito_automatico',
              cubre_cuotas: d.cubre, saldo_antes: antes.pendiente,
              saldo_despues: Math.max(0, antes.pendiente - d.monto),
              nota: `${cuenta.nombre} lo descontó solo.`,
            }),
            id: idDeterminista(d.fecha, `${semilla}:abono`),
          };
          await guardar('abonos', abono);
          propios = [...propios, abono];

          const despues = estadoPlan(plan, propios, hoy());
          await guardar('compras_diferidas', {
            ...c,
            cuotas_pagadas: despues.cuotasPagadas,
            cuotas_pendientes: plan.length - despues.cuotasPagadas,
            saldo_pendiente: despues.pendiente,
            fecha_proxima_cuota: despues.proxima?.fecha ?? null,
            pagada: despues.saldada,
          });
          anotados.push({ nombre: `${c.descripcion}: ${cuenta.nombre} descontó`, monto: d.monto, fecha: d.fecha, pantalla: 'cuotas' });
        }

        // Lo que quedó sin poder cobrarse: se dice, no se esconde.
        const falta = estadoPlan(plan, propios, hoy()).vencidas.reduce((t, f) => t + f.pendiente, 0);
        if (falta > 0 && debitos.length) {
          anotados.push({
            nombre: `${c.descripcion}: faltan`, monto: falta, fecha: hoy(), pantalla: 'cuotas',
            nota: `${cuenta.nombre} los descuenta cuando entre plata`,
          });
        }
      }
      if (anotados.length) cobrosSolos = [...cobrosSolos, ...anotados];
    } catch (e) {
      console.error('No se pudieron anotar los descuentos automáticos', e);
    } finally {
      debitando = false;
    }
  }

  /**
   * Categorías que llegaron por nombre (ver lib/categorias.js).
   *
   * Lo que se anota desde fuera del celular —el asistente cuadrando las
   * cuentas, o la IA leyendo un extracto— no conoce los ids de las
   * categorías de este dispositivo. Deja el nombre, y aquí se cambia por el
   * id. Si el nombre no existe (se renombró o se borró), se deja quieto.
   */
  let resolviendo = false;
  // El 4x1000 automático siempre es 4x1000: sin esto, cada uno contaba como
  // "gasto sin clasificar" y el aviso de gastos sin categoría se inflaba.
  const nombreDeCategoria = (m) =>
    m.categoria_nombre || (m.concepto === '4x1000' && m.movimiento_padre_id ? '4x1000 (GMF)' : null);

  async function resolverCategoriasPorNombre() {
    if (resolviendo || !categorias.length) return;
    const conNombre = movimientos.filter((m) => !m.categoria_id && nombreDeCategoria(m));
    if (!conNombre.length) return;
    resolviendo = true;
    try {
      for (const m of conNombre) {
        const cat = buscarCategoria(categorias, nombreDeCategoria(m),
          m.tipo === 'ingreso' ? 'ingreso' : 'egreso');
        if (cat) await guardar('movimientos', { ...m, categoria_id: cat.id, categoria_nombre: null });
      }
    } catch (e) {
      console.error('No se pudieron poner las categorías por nombre', e);
    } finally {
      resolviendo = false;
    }
  }

  /**
   * Anota solos los cobros de las suscripciones que se cobran solas.
   *
   * Netflix en Nu se cobra aunque uno no abra la app. Si la app no lo
   * anotara, el saldo de la tarjeta estaría mal desde ese día. Por eso, al
   * abrir, se revisa qué cobros ya debieron pasar y se anotan.
   *
   * Nunca se duplica: cada cobro tiene un id fijo (ver lib/recurrentes.js).
   * Si ya existe —o se borró a propósito—, se deja quieto. Y nunca en
   * silencio: lo que se anotó solo se dice arriba, con cifra y fecha.
   */
  let anotando = false;
  async function anotarCobrosAutomaticos() {
    if (anotando || !recurrentes.length) return;
    anotando = true;
    try {
      const candidatos = cobrosParaAnotar(recurrentes);
      if (!candidatos.length) return;
      const ya = await db.movimientos.bulkGet(candidatos.map((c) => c.id));
      const nuevos = candidatos.filter((_, i) => !ya[i]);
      if (!nuevos.length) return;

      for (const { rec, fecha } of nuevos) {
        const cuenta = cuentas.find((c) => c.id === rec.cuenta_id);
        const base = movimientoDelCobro(rec, fecha);
        // El débito automático sale del banco: paga 4x1000. La tarjeta no.
        const gmf = rec.cobro === COBRO.DEBITO
          ? gmfDe({
              tipo: 'egreso', monto: base.monto, cuentaOrigen: cuenta,
              entidad: entidades.find((e) => e.id === cuenta?.entidad_id) ?? null,
              metodoPago: base.metodo_pago,
            }).monto
          : 0;
        const mov = crearMovimiento({ ...base, gmf });
        await guardar('movimientos', mov);
        if (gmf > 0) {
          await guardar('movimientos', {
            ...crearMovimiento(movimientoGmf(mov, gmf)),
            id: idDelGmfDelCobro(rec, fecha),
            recurrente_id: rec.id,
          });
        }
      }
      cobrosSolos = [...cobrosSolos, ...nuevos.map(({ rec, fecha }) => ({
        nombre: rec.nombre, monto: rec.monto, fecha, variable: rec.monto_variable,
      }))];
    } catch (e) {
      console.error('No se pudieron anotar los cobros automáticos', e);
    } finally {
      anotando = false;
    }
  }

  const fijos = $derived(resumenFijos(recurrentes));

  /**
   * Libre este mes: la plata en cuentas menos lo que falta pagar este mes.
   * Es la cifra grande de arriba porque es con la que uno decide. El balance
   * (lo que tienes menos TODO lo que debes) va debajo: es cierto, pero nadie
   * paga hoy todas sus cuotas. Ver lib/libre.js.
   */
  let verDesglose = $state(false);
  const disponible = $derived(disponibleEn(cuentas, saldos));
  const compromisos = $derived(compromisosDelMes({
    compras, abonos, tarjetas, recurrentes, prestamos, contratos,
    movimientos, categorias, hoy: hoy(),
  }));
  const libre = $derived(disponible - compromisos.total);

  $effect(() => {
    cargar();
    // El repositorio avisa cada vez que algo cambia, venga de esta pantalla
    // o de la sincronización.
    return alCambiar(cargar);
  });

  // Arranca el motor de sincronización solo si ya está configurado. Sin
  // configurar, la app funciona igual: guarda todo aquí y no intenta subir.
  $effect(() => {
    const quitarObservador = sincronizador.observar((s) => {
      estadoSync = s;
      pendientesDeSubir().then((n) => (pendientes = n));
    });
    if (estaConfigurada()) sincronizador.iniciar();
    return () => {
      quitarObservador();
      sincronizador.detener();
    };
  });

  /**
   * Los avisos. Se recalculan solos cada vez que cambia algo, porque son
   * cuentas locales: no hay que pedirle permiso a ningun servidor.
   */
  const avisos = $derived.by(() => {
    if (cargando || !movimientos.length) return [];
    return evaluar(
      fotografiar({
        movimientos, cuentas, categorias, tarjetas, contratos, prestamos,
        compras, abonos, recurrentes,
        compromisosMes: compromisos.total,
      }),
      descartados
    );
  });

  /** Lo que se muestra en el chip de estado, en palabras y no en jerga. */
  const textoSync = $derived.by(() => {
    if (!estaConfigurada()) return pendientes > 0 ? `${pendientes} sin subir` : null;
    if (estadoSync.estado === ESTADO.SINCRONIZANDO) return 'Subiendo…';
    if (estadoSync.estado === ESTADO.SIN_CONEXION) {
      return pendientes > 0 ? `${pendientes} sin subir` : 'Sin conexión';
    }
    if (estadoSync.estado === ESTADO.ERROR) return 'Revisa la configuración';
    return pendientes > 0 ? `${pendientes} sin subir` : null;
  });
</script>

<div class="app">
  <header>
    <div class="saldo">
      <button class="rotulo boton-libre" onclick={() => (verDesglose = !verDesglose)} aria-expanded={verDesglose}>
        Libre este mes <span aria-hidden="true">{verDesglose ? '▴' : '▾'}</span>
      </button>
      <p class="cifra total" class:en-rojo={libre < 0} aria-hidden="true">{formatear(libre)}</p>
      <p class="solo-lectores">Te quedan libres este mes {formatearAria(libre)}</p>
      <p class="balance">
        Balance: <strong class="cifra">{formatear(patrimonio)}</strong>
        <span class="balance-nota">lo que tienes menos todo lo que debes</span>
      </p>
    </div>

    <div class="estado">
      {#if textoSync}
        <!-- Gris, nunca rojo: estar sin señal es lo normal, no una falla. -->
        <button
          class="chip-sync"
          class:problema={estadoSync.estado === ESTADO.ERROR}
          onclick={() => (configAbierta = true)}
        >
          <span class="bolita" aria-hidden="true"></span>
          {textoSync}
        </button>
      {/if}
      <button class="ajustes" onclick={() => (configAbierta = true)} aria-label="Configuración">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.8" />
          <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"
                stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      </button>
    </div>
  </header>

  {#if verDesglose}
    <!-- El desglose: una cifra que no se puede revisar no se cree. -->
    <div class="desglose">
      <div class="linea">
        <span>En efectivo, bancos y billeteras</span>
        <span class="cifra">{formatear(disponible)}</span>
      </div>
      {#each compromisos.items as it}
        <div class="linea menos" class:atrasada={it.vencida}>
          <span>
            {it.nombre}{#if it.fecha}{' · '}{new Date(it.fecha + 'T12:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })}{/if}{#if it.vencida}{' · atrasada'}{/if}
          </span>
          <span class="cifra">−{formatear(it.monto)}</span>
        </div>
        {#if it.nota}<p class="nota-desglose">{it.nota}</p>{/if}
      {/each}
      <div class="linea total-linea">
        <span>Libre este mes</span>
        <span class="cifra">{formatear(libre)}</span>
      </div>
      <p class="nota-desglose">
        Solo se resta lo que falta pagar este mes. Lo que te deben se suma
        cuando llegue, no antes.
      </p>
    </div>
  {/if}

  {#if cuentas.length}
    <div class="cuentas">
      {#each cuentasVisibles as c (c.id)}
        <button class="tarjeta-cuenta" onclick={() => (cuentasAbiertas = true)}>
          <span class="nombre">{c.icono ?? ''} {c.nombre}</span>
          <span class="cifra monto-cuenta" class:deuda={CUENTAS_PASIVO.includes(c.tipo)}>
            {formatear(saldos[c.id] ?? 0)}
          </span>
        </button>
      {/each}
      <!-- Siempre visible, aunque no haya compras a cuotas: es la única
           puerta para anotar la primera. Escondida, nadie la encontraría. -->
      <button class="tarjeta-cuenta" onclick={() => (cuotasAbiertas = true)}>
        <span class="nombre">🧾 Cuotas del mes</span>
        <span class="cifra monto-cuenta" class:deuda={cuotasEsteMes.total > 0}>
          {formatearCorto(cuotasEsteMes.total)}
        </span>
      </button>
      <button class="tarjeta-cuenta" onclick={() => (suscripcionesAbiertas = true)}>
        <span class="nombre">🔁 Fijos al mes</span>
        <span class="cifra monto-cuenta">{formatearCorto(fijos.mensual)}</span>
      </button>
      <button class="tarjeta-cuenta" onclick={() => (prestamosAbiertos = true)}>
        <span class="nombre">🤝 Préstamos</span>
        <span class="cifra monto-cuenta">
          {formatearCorto(deudasPersonas.meDeben)}
        </span>
      </button>
      <!-- Siempre visible al final del carrusel: es la única forma de
           descubrir que se pueden agregar más cuentas. -->
      <button class="tarjeta-cuenta agregar-cuenta" onclick={() => (cuentasAbiertas = true)}>
        <span class="mas" aria-hidden="true">+</span>
        <span class="nombre">Agregar cuenta</span>
      </button>
    </div>
  {/if}

  {#if gastoDelMes > 0}
    <p class="resumen-mes">
      Este mes llevas <strong class="cifra">{formatear(gastoDelMes)}</strong> en gastos
    </p>
  {/if}

  <Avisos {avisos} alDescartar={descartar} />

  {#if cobrosSolos.length}
    <div class="cobros-solos" role="status">
      <button class="cobros-texto" onclick={() => {
        if (cobrosSolos.some((c) => c.pantalla === 'cuotas')) cuotasAbiertas = true;
        else suscripcionesAbiertas = true;
      }}>
        <strong>Se anotaron solos:</strong>
        {#each cobrosSolos as c, i}
          {c.nombre} {formatear(c.monto)}{c.variable ? ' (revisa el monto)' : ''}{c.nota ? ` (${c.nota})` : ''}{i < cobrosSolos.length - 1 ? ' · ' : ''}
        {/each}
      </button>
      <button class="cobros-cerrar" onclick={() => (cobrosSolos = [])} aria-label="Cerrar">×</button>
    </div>
  {/if}

  <main>
    {#if cargando}
      <p class="vacio">Cargando…</p>
    {:else if porDia.length === 0}
      <div class="vacio">
        <p class="vacio-titulo">Todavía no hay nada anotado</p>
        <p class="vacio-texto">
          Toca el botón de abajo y anota tu primer gasto.
          Con el teclado grande y la tecla de tres ceros toma unos segundos.
        </p>
      </div>
    {:else}
      {#each porDiaVisible as [fecha, delDia] (fecha)}
        <h2 class="dia">{etiquetaDia(fecha)}</h2>
        {#each delDia as m (m.id)}
          <button class="fila-tocable" onclick={() => (movimientoAbierto = m)}>
            <FilaMovimiento
              movimiento={m}
              categoria={categoriasPorId[m.categoria_id]}
              cuenta={cuentasPorId[m.cuenta_origen ?? m.cuenta_destino]}
            />
          </button>
        {/each}
      {/each}
      {#if porDia.length > diasVisibles}
        <button class="ver-mas" onclick={() => (diasVisibles += 60)}>
          Ver días anteriores
        </button>
      {/if}
    {/if}
  </main>

  <nav class="barra">
    <button class="nav-item activo" aria-current="page">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 10.5 12 3l9 7.5M5.5 9.5V20h13V9.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>Inicio</span>
    </button>
    <button class="nav-item" onclick={() => (cuentasAbiertas = true)}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="6" width="18" height="13" rx="2.5" stroke="currentColor" stroke-width="1.8"/>
        <path d="M3 10.5h18" stroke="currentColor" stroke-width="1.8"/>
      </svg>
      <span>Cuentas</span>
    </button>
    <span class="hueco" aria-hidden="true"></span>
    <button class="nav-item" onclick={() => (informesAbiertos = true)}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 19V11M12 19V5M18 19v-6" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>
      </svg>
      <span>Informes</span>
    </button>
    <button class="nav-item" onclick={() => (contratosAbiertos = true)}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
        <path d="M14 3v5h5M9 13h6M9 17h4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
      </svg>
      <span>Contratos</span>
    </button>
  </nav>

  <button class="fab-foto" onclick={() => (camaraAbierta = true)} aria-label="Leer una factura con la cámara">
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2.2l1-1.7A1 1 0 0 1 8.6 5h6.8a1 1 0 0 1 .9.3l1 1.7h2.2A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-9Z"
            stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
      <circle cx="12" cy="12.6" r="3.2" stroke="currentColor" stroke-width="1.7"/>
    </svg>
  </button>

  <button class="fab" onclick={() => (hojaAbierta = true)} aria-label="Registrar movimiento">
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
    </svg>
  </button>

  <HojaRegistro bind:abierta={hojaAbierta} {cuentas} {categorias} {entidades} />
  <Configuracion bind:abierta={configAbierta} {sincronizador} alAbrirImportar={() => (importarAbierto = true)} />
  <PantallaCuentas bind:abierta={cuentasAbiertas} {cuentas} {saldos} {entidades} />
  <HojaComprobante bind:abierta={camaraAbierta} {cuentas} {categorias} {api} />
  <PantallaInformes bind:abierta={informesAbiertos} {movimientos} {categorias} {cuentas} />
  <PantallaImportar bind:abierta={importarAbierto} {cuentas} alImportar={cargar} />
  <HojaMovimiento bind:movimiento={movimientoAbierto} {cuentas} {categorias} {entidades} alCambiar={cargar} alIrA={irA} />
  <PantallaContratos bind:abierta={contratosAbiertos} {contratos} {cuentas} alCambiar={cargar} />
  <PantallaPrestamos bind:abierta={prestamosAbiertos} bind:enfocar={deudaEnfocada} {prestamos} {cuentas} {entidades} {abonos} alCambiar={cargar} />
  <PantallaCuotas bind:abierta={cuotasAbiertas} bind:enfocar={compraEnfocada} {compras} {cuentas} {tarjetas} {entidades} {abonos} {categorias} alCambiar={cargar} />
  <PantallaSuscripciones
    bind:abierta={suscripcionesAbiertas}
    {recurrentes} {cuentas} {categorias} {entidades}
    alCambiar={cargar}
    alAbrirMovimiento={(m) => (movimientoAbierto = m)}
  />
</div>

<style>
  .ver-mas {
    display: block;
    width: 100%;
    margin-top: var(--e-4);
    padding: var(--e-3);
    border: 1px solid var(--border);
    border-radius: var(--r-md);
    color: var(--ink-2);
    font-size: var(--t-sm);
  }
  .boton-libre { display: inline-flex; align-items: center; gap: var(--e-1); padding: 0; background: none }
  .total.en-rojo { color: var(--alerta) }
  .balance { font-size: var(--t-sm); color: var(--ink-3); margin-top: var(--e-1) }
  .balance strong { color: var(--ink-2); font-weight: var(--w-semibold) }
  .balance-nota { display: block; font-size: var(--t-xs) }

  .desglose {
    margin: 0 var(--gutter) var(--e-4);
    padding: var(--e-3) var(--e-4);
    border-radius: var(--r-lg);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    gap: var(--e-2);
    font-size: var(--t-sm);
    animation: aparecer-desglose var(--d-rapida) var(--ease-entrada);
  }
  @keyframes aparecer-desglose { from { opacity: 0; transform: translateY(-4px) } to { opacity: 1; transform: none } }
  .linea { display: flex; justify-content: space-between; gap: var(--e-3) }
  .linea .cifra { white-space: nowrap; font-weight: var(--w-medium) }
  .linea.menos { color: var(--ink-2) }
  .linea.atrasada { color: var(--alerta) }
  .total-linea { border-top: 1px solid var(--border); padding-top: var(--e-2); font-weight: var(--w-semibold) }
  .nota-desglose { font-size: var(--t-xs); color: var(--ink-3); line-height: var(--lh-loose) }

  .cobros-solos {
    margin: 0 var(--gutter) var(--e-3);
    display: flex;
    align-items: flex-start;
    gap: var(--e-2);
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--bg-sunken);
    border-left: 3px solid var(--acento);
    font-size: var(--t-sm);
  }
  .cobros-texto { flex: 1; text-align: left; line-height: var(--lh-loose); color: var(--ink-2) }
  .cobros-cerrar {
    width: 32px;
    height: 32px;
    border-radius: var(--r-full);
    font-size: var(--t-lg);
    color: var(--ink-3);
    flex-shrink: 0;
  }

  header {
    padding: calc(var(--e-6) + var(--safe-t)) var(--gutter) var(--e-4);
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--e-3);
  }

  .rotulo {
    font-size: var(--t-sm);
    color: var(--ink-3);
    margin-bottom: var(--e-1);
  }

  .total {
    font-size: var(--t-3xl);
    font-weight: var(--w-bold);
    letter-spacing: var(--track-tight);
    line-height: var(--lh-tight);
  }

  .estado { display: flex; align-items: center; gap: var(--e-2) }

  .chip-sync {
    display: inline-flex;
    align-items: center;
    gap: var(--e-1);
    font-size: var(--t-xs);
    color: var(--ink-3);
    background: var(--bg-sunken);
    padding: var(--e-1) var(--e-2);
    border-radius: var(--r-full);
  }
  .chip-sync.problema { color: var(--alerta); background: var(--alerta-suave) }

  .bolita {
    width: 6px;
    height: 6px;
    border-radius: var(--r-full);
    background: var(--sync-pendiente);
  }
  .chip-sync.problema .bolita { background: var(--alerta) }

  .ajustes {
    width: var(--tap);
    height: var(--tap);
    display: grid;
    place-items: center;
    color: var(--ink-3);
    border-radius: var(--r-full);
    margin-right: calc(var(--e-3) * -1);
  }

  /* ── Cuentas ── */
  .cuentas {
    display: flex;
    gap: var(--e-2);
    padding: 0 var(--gutter) var(--e-4);
    overflow-x: auto;
    /* Que cada tarjeta quede encuadrada al soltar el dedo. */
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
  }
  .cuentas::-webkit-scrollbar { display: none }

  .tarjeta-cuenta {
    flex: 0 0 auto;
    scroll-snap-align: start;
    min-width: 132px;
    padding: var(--e-3);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    display: flex;
    flex-direction: column;
    gap: var(--e-1);
    box-shadow: var(--sombra-1);
    text-align: left;
    transition: transform var(--d-instant) var(--ease-salida);
  }
  .tarjeta-cuenta:active { transform: scale(0.97) }

  .agregar-cuenta {
    background: transparent;
    border-style: dashed;
    border-color: var(--border-strong);
    box-shadow: none;
    justify-content: center;
    color: var(--ink-3);
  }
  .mas { font-size: var(--t-xl); line-height: 1 }
  .nombre {
    font-size: var(--t-xs);
    color: var(--ink-3);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .monto-cuenta {
    font-size: var(--t-lg);
    font-weight: var(--w-semibold);
  }
  .monto-cuenta.deuda { color: var(--peligro) }

  .resumen-mes {
    padding: 0 var(--gutter) var(--e-4);
    font-size: var(--t-sm);
    color: var(--ink-2);
  }

  /* ── Lista ── */
  main {
    flex: 1;
    /* Espacio para que el botón flotante no tape la última fila. */
    padding-bottom: calc(var(--h-nav) + var(--e-10) + var(--safe-b));
  }

  /* La fila entera es un boton, pero no debe parecerlo: se conserva el
     ancho completo y no se le pone ningun adorno de boton. */
  .fila-tocable {
    display: block;
    width: 100%;
    text-align: left;
    transition: background var(--d-instant) var(--ease-estandar);
  }
  .fila-tocable:active { background: var(--bg-sunken) }

  .dia {
    position: sticky;
    top: 0;
    z-index: var(--z-pegajoso);
    padding: var(--e-2) var(--gutter);
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--ink-3);
    background: var(--bg-canvas);
  }

  .vacio {
    padding: var(--e-16) var(--gutter);
    text-align: center;
    color: var(--ink-3);
  }
  .vacio-titulo {
    font-size: var(--t-lg);
    font-weight: var(--w-semibold);
    color: var(--ink-2);
    margin-bottom: var(--e-2);
  }
  .vacio-texto {
    font-size: var(--t-sm);
    max-width: 30ch;
    margin-inline: auto;
    line-height: var(--lh-loose);
  }

  /* ── Botón flotante ── */
  .fab {
    position: fixed;
    bottom: calc(var(--h-nav) + var(--e-3));
    right: max(var(--gutter), calc(50vw - var(--w-app) / 2 + var(--gutter)));
    z-index: var(--z-fab);
    width: 60px;
    height: 60px;
    border-radius: var(--r-full);
    background: var(--acento);
    color: var(--on-acento);
    display: grid;
    place-items: center;
    box-shadow: var(--sombra-3);
    transition: transform var(--d-rapida) var(--ease-resorte);
  }
  .fab:active { transform: scale(0.92) }

  /* La cámara va al lado del botón principal, más pequeña: escribir a mano
     sigue siendo el camino rápido; la foto es para cuando hay una factura. */
  .fab-foto {
    position: fixed;
    bottom: calc(var(--h-nav) + var(--e-3) + 7px);
    right: max(calc(var(--gutter) + 68px),
               calc(50vw - var(--w-app) / 2 + var(--gutter) + 68px));
    z-index: var(--z-fab);
    width: 46px;
    height: 46px;
    border-radius: var(--r-full);
    background: var(--bg-surface);
    color: var(--ink-2);
    border: 1px solid var(--border-strong);
    display: grid;
    place-items: center;
    box-shadow: var(--sombra-2);
    transition: transform var(--d-rapida) var(--ease-resorte);
  }
  .fab-foto:active { transform: scale(0.92) }

  /* ── Barra inferior ──
     Va abajo porque es donde llega el pulgar. El hueco del centro es para
     que el boton flotante no tape ningun destino. */
  .barra {
    position: fixed;
    bottom: 0;
    inset-inline: 0;
    z-index: var(--z-nav);
    margin-inline: auto;
    max-width: var(--w-app);
    display: grid;
    grid-template-columns: 1fr 1fr 72px 1fr 1fr;
    align-items: center;
    padding-bottom: var(--safe-b);
    background: var(--bg-surface);
    border-top: 1px solid var(--border);
  }
  .nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: var(--e-2) 0;
    min-height: var(--h-nav);
    justify-content: center;
    color: var(--ink-3);
    font-size: var(--t-2xs);
    transition: color var(--d-rapida) var(--ease-estandar);
  }
  .nav-item.activo { color: var(--acento) }
  .nav-item:active { color: var(--ink-1) }
</style>
